import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ZITI_DATA_SERVICE, ZitiDataService } from 'ziti-console-lib';
import { CreationStatus, ServiceTemplate } from './service-template.model';
import { buildConfigPayloads, ConfigPayload, TemplateFormData } from './template-form.util';
import {
    MICROSEGMENT_SERVICE_ID_TAG_KEY,
    MICROSEGMENT_SOURCE_TAG_KEY,
    MICROSEGMENT_SOURCE_TRAFFIC,
} from './microsegment-mapping.util';

export interface CreateFromTemplateParams {
    template: ServiceTemplate;
    serviceName: string;
    serviceRoleAttributes: string[];
    encryptionRequired: boolean;
    terminatorStrategy: string;
    formData: TemplateFormData;
    dialPolicyAttributes: string[];
    bindPolicyAttributes: string[];
}

/**
 * Create a service from already-built config payloads (rather than a template +
 * form data). Used by flows that assemble intercept/host configs directly — e.g.
 * microsegmentation autofill from Traffic Analysis.
 */
export interface CreateFromConfigsParams {
    serviceName: string;
    configPayloads: ConfigPayload[];
    dialPolicyAttributes?: string[];
    bindPolicyAttributes?: string[];
    serviceRoleAttributes?: string[];
    encryptionRequired?: boolean;
    terminatorStrategy?: string;
    serviceTags?: Record<string, any>;
}

export interface CreationResult {
    hasErrors: boolean;
    service?: any;
}

/** The shape both public entry points normalize to before running the pipeline. */
interface NormalizedCreateParams {
    serviceName: string;
    serviceRoleAttributes: string[];
    encryptionRequired: boolean;
    terminatorStrategy: string;
    serviceTags: Record<string, any>;
    configPayloads: ConfigPayload[];
    dialPolicyAttributes: string[];
    bindPolicyAttributes: string[];
}

const toRoleSelector = (attr: string): string => (attr.startsWith('@') || attr.startsWith('#') ? attr : `#${attr}`);

/**
 * Creates a complete OpenZiti service (service + configs + dial/bind policies),
 * reporting progress through a per-step status list.
 *
 * Naming convention (kept consistent with hand-created services so templated and
 * manually-configured services remain interchangeable):
 *   - configs:  `<serviceName>-<configTypeName>`
 *   - policies: `<serviceName>-dial`, `<serviceName>-bind`
 */
@Injectable({
    providedIn: 'root',
})
export class ServiceFromTemplateService {
    constructor(@Inject(ZITI_DATA_SERVICE) private zitiService: ZitiDataService) {}

    createServiceFromTemplate(params: CreateFromTemplateParams): {
        status$: Observable<CreationStatus[]>;
        result: Promise<CreationResult>;
    } {
        const normalized: NormalizedCreateParams = {
            serviceName: params.serviceName,
            serviceRoleAttributes: params.serviceRoleAttributes,
            encryptionRequired: params.encryptionRequired,
            terminatorStrategy: params.terminatorStrategy,
            serviceTags: { serviceTemplate: params.template.name },
            configPayloads: buildConfigPayloads(params.template.configTypes, params.formData, params.serviceName),
            dialPolicyAttributes: params.dialPolicyAttributes,
            bindPolicyAttributes: params.bindPolicyAttributes,
        };
        return this.run(normalized);
    }

    createServiceFromConfigs(params: CreateFromConfigsParams): {
        status$: Observable<CreationStatus[]>;
        result: Promise<CreationResult>;
    } {
        const normalized: NormalizedCreateParams = {
            serviceName: params.serviceName,
            serviceRoleAttributes: params.serviceRoleAttributes ?? [],
            encryptionRequired: params.encryptionRequired ?? true,
            terminatorStrategy: params.terminatorStrategy ?? '',
            serviceTags: params.serviceTags ?? {},
            configPayloads: params.configPayloads ?? [],
            dialPolicyAttributes: params.dialPolicyAttributes ?? [],
            bindPolicyAttributes: params.bindPolicyAttributes ?? [],
        };
        return this.run(normalized);
    }

    /** Human-readable description of an API error, used to render the expandable error details. */
    formatErrorDetails(error: any): string {
        if (!error) return '';
        if (typeof error === 'string') return error;
        if (error.error) {
            return typeof error.error === 'string' ? error.error : JSON.stringify(error.error, null, 2);
        }
        if (error.message) return error.message;
        return JSON.stringify(error, null, 2);
    }

    private run(params: NormalizedCreateParams): {
        status$: Observable<CreationStatus[]>;
        result: Promise<CreationResult>;
    } {
        const statuses = this.buildInitialStatuses(params);
        const statusSubject = new BehaviorSubject<CreationStatus[]>(statuses);

        const update = (
            index: number,
            status: CreationStatus['status'],
            message?: string,
            errorDetails?: any
        ): void => {
            statuses[index] = { ...statuses[index], status, message, errorDetails };
            statusSubject.next([...statuses]);
        };

        const result = this.runPipeline(params, statuses, update).finally(() => statusSubject.complete());

        return { status$: statusSubject.asObservable(), result };
    }

    // Steps read as tense-neutral noun phrases (the status icon - spinner, check or cross - carries the
    // in-progress / done / failed state), so a step reads correctly both while it runs and once finished.
    private buildInitialStatuses(params: NormalizedCreateParams): CreationStatus[] {
        const statuses: CreationStatus[] = [{ step: `Service "${params.serviceName}"`, status: 'pending' }];

        params.configPayloads.forEach((payload) => {
            statuses.push({ step: `Configuration "${payload.name}"`, status: 'pending' });
        });

        // Note: attaching the configs to the service is done in the pipeline but is not listed as its own
        // step - it is internal wiring (a service edit), not a resource the user created.

        if (params.dialPolicyAttributes.length > 0) {
            statuses.push({ step: `Dial policy "${params.serviceName}-dial"`, status: 'pending' });
        }

        if (params.bindPolicyAttributes.length > 0) {
            statuses.push({ step: `Bind policy "${params.serviceName}-bind"`, status: 'pending' });
        }

        return statuses;
    }

    private async runPipeline(
        params: NormalizedCreateParams,
        statuses: CreationStatus[],
        update: (index: number, status: CreationStatus['status'], message?: string, errorDetails?: any) => void
    ): Promise<CreationResult> {
        let hasErrors = false;
        let index = 0;

        // Step 1: create the service.
        update(index, 'in_progress');
        let service: any;
        try {
            service = await this.zitiService.post('services', {
                name: params.serviceName,
                roleAttributes: params.serviceRoleAttributes,
                encryptionRequired: params.encryptionRequired,
                terminatorStrategy: params.terminatorStrategy,
                tags: params.serviceTags,
            });
            service = service?.data || service;
            update(index, 'success', `Service ID: ${service?.id}`);
        } catch (error) {
            update(index, 'error', 'Failed to create service', error);
            return { hasErrors: true };
        }
        index++;

        // For a microsegment workflow, link every resource back to this service by stamping its Ziti id.
        // That shared tag is what lets the service + its configs + policies (+ later its identities) be
        // assembled and managed as one microsegment. Only applied when the service carries the
        // microsegment-source marker, so template and plain simple-service creates are unaffected.
        const memberTags =
            params.serviceTags?.[MICROSEGMENT_SOURCE_TAG_KEY] === MICROSEGMENT_SOURCE_TRAFFIC && service?.id
                ? {
                      [MICROSEGMENT_SOURCE_TAG_KEY]: MICROSEGMENT_SOURCE_TRAFFIC,
                      [MICROSEGMENT_SERVICE_ID_TAG_KEY]: service.id,
                  }
                : undefined;

        // Step 2: create configs sequentially.
        const createdConfigIds: string[] = [];

        for (const configPayload of params.configPayloads) {
            update(index, 'in_progress');
            try {
                const payload = memberTags
                    ? { ...configPayload, tags: { ...(configPayload.tags ?? {}), ...memberTags } }
                    : configPayload;
                const created = await this.zitiService.post('configs', payload);
                const createdConfig = created?.data || created;
                createdConfigIds.push(createdConfig.id);
                update(index, 'success', `Config ID: ${createdConfig.id}`);
            } catch (error) {
                update(index, 'error', 'Failed to create configuration', error);
                hasErrors = true;
            }
            index++;
        }

        // Step 3: attach the created configs to the service. Required for the service to do anything, but
        // not shown as its own step (it is a service edit, not a created resource); a failure here still
        // marks the whole create as errored.
        if (params.configPayloads.length > 0) {
            try {
                await this.zitiService.patch(
                    'services',
                    { name: service.name, roleAttributes: service.roleAttributes, configs: createdConfigIds },
                    service.id
                );
            } catch (error) {
                // Attaching the configs is internal wiring, not its own status row, so when it fails
                // surface the error on the steps that will not run now (the policies) instead of
                // returning silently and leaving them stuck on the spinner.
                for (let i = index; i < statuses.length; i++) {
                    update(i, 'error', 'Failed to link configurations', error);
                }
                return { hasErrors: true, service };
            }
        }

        const serviceRoleSelector =
            params.serviceRoleAttributes.length > 0 ? `#${params.serviceRoleAttributes[0]}` : `@${service.id}`;

        // Step 4: dial policy.
        if (params.dialPolicyAttributes.length > 0) {
            update(index, 'in_progress');
            try {
                const createdPolicy = await this.zitiService.post('service-policies', {
                    name: `${params.serviceName}-dial`,
                    type: 'Dial',
                    semantic: 'AnyOf',
                    identityRoles: params.dialPolicyAttributes.map(toRoleSelector),
                    serviceRoles: [serviceRoleSelector],
                    ...(memberTags ? { tags: memberTags } : {}),
                });
                update(index, 'success', `Policy ID: ${(createdPolicy?.data || createdPolicy)?.id}`);
            } catch (error) {
                update(index, 'error', 'Failed to create Dial policy', error);
                hasErrors = true;
            }
            index++;
        }

        // Step 5: bind policy.
        if (params.bindPolicyAttributes.length > 0) {
            update(index, 'in_progress');
            try {
                const createdPolicy = await this.zitiService.post('service-policies', {
                    name: `${params.serviceName}-bind`,
                    type: 'Bind',
                    semantic: 'AnyOf',
                    identityRoles: params.bindPolicyAttributes.map(toRoleSelector),
                    serviceRoles: [serviceRoleSelector],
                    ...(memberTags ? { tags: memberTags } : {}),
                });
                update(index, 'success', `Policy ID: ${(createdPolicy?.data || createdPolicy)?.id}`);
            } catch (error) {
                update(index, 'error', 'Failed to create Bind policy', error);
                hasErrors = true;
            }
        }

        return { hasErrors, service };
    }
}
